import css from "./Sidebar.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {RouteLocation} from "@opendaw/lib-jsx"
import {getCreditsInfo, onCreditsChange} from "@/ui/credits/CreditStore"

const className = Html.adoptStyleSheet(css, "Sidebar")

type NavItem = {
    icon: string
    label: string
    path: string
    external?: boolean
    prominent?: boolean
}

const NAV_ITEMS: NavItem[] = [
    {icon: "home", label: "Home", path: "/"},
    {icon: "add", label: "Studio", path: "/studio", prominent: true},
    {icon: "bolt", label: "Quick Create", path: "/create"},
    {icon: "explore", label: "Explore", path: "/explore"},
    {icon: "library_music", label: "Library", path: "/library"},
]

// Inject Material Symbols font
if (!document.querySelector("#material-symbols-sidebar")) {
    const link = document.createElement("link")
    link.id = "material-symbols-sidebar"
    link.rel = "stylesheet"
    link.href = "https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap"
    document.head.appendChild(link)
}

export const Sidebar = ({defaultCollapsed}: {defaultCollapsed: boolean}) => {
    let collapsed = defaultCollapsed

    const container: HTMLDivElement = <div className={`${className}${collapsed ? " collapsed" : ""}`}/> as unknown as HTMLDivElement

    // Logo
    const logoRow: HTMLDivElement = (
        <div className="logo-row">
            <img className="logo-icon" src="/logo.png" alt="Logo"/>
            <span className="logo-text">MusicFlow</span>
        </div>
    ) as unknown as HTMLDivElement

    // Collapse toggle
    const collapseIcon: HTMLSpanElement = (
        <span className="material-symbols-rounded">{collapsed ? "chevron_right" : "chevron_left"}</span>
    ) as unknown as HTMLSpanElement

    const collapseBtn: HTMLButtonElement = (
        <button className="collapse-btn" title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapseIcon}
        </button>
    ) as unknown as HTMLButtonElement

    collapseBtn.onclick = () => {
        collapsed = !collapsed
        container.classList.toggle("collapsed", collapsed)
        collapseIcon.textContent = collapsed ? "chevron_right" : "chevron_left"
        collapseBtn.title = collapsed ? "Expand sidebar" : "Collapse sidebar"
    }

    logoRow.onclick = () => {
        RouteLocation.get().navigateTo("/")
    }

    // Navigation
    const nav: HTMLElement = <nav/> as unknown as HTMLElement

    const navButtons: HTMLButtonElement[] = []

    function updateActiveState(): void {
        const currentPath = RouteLocation.get().path
        for (const btn of navButtons) {
            const path = btn.dataset.path || ""
            const isActive = currentPath === path || (path !== "/" && currentPath.startsWith(path))
            btn.classList.toggle("active", isActive)
        }
    }

    for (const item of NAV_ITEMS) {
        const btnClass = item.prominent ? "nav-item prominent" : "nav-item"
        const btn: HTMLButtonElement = (
            <button className={btnClass} data-path={item.path} title={item.label}>
                <span className="material-symbols-rounded nav-icon">{item.icon}</span>
                <span className="nav-label">{item.prominent ? `+ ${item.label}` : item.label}</span>
            </button>
        ) as unknown as HTMLButtonElement

        btn.onclick = () => {
            if (item.external) {
                window.open(item.path, "_blank")
            } else {
                RouteLocation.get().navigateTo(item.path)
                updateActiveState()
            }
        }

        navButtons.push(btn)
        nav.appendChild(btn)
    }

    // Spacer
    const spacer: HTMLDivElement = <div className="nav-spacer"/> as unknown as HTMLDivElement
    nav.appendChild(spacer)



    // Footer with credits
    const creditsEl: HTMLSpanElement = <span className="user-credits"/> as unknown as HTMLSpanElement

    function updateCredits(): void {
        const info = getCreditsInfo()
        if (info.unlimited) {
            creditsEl.textContent = "Unlimited"
        } else {
            creditsEl.textContent = `${info.daily + info.purchased} credits`
        }
    }
    updateCredits()
    onCreditsChange(updateCredits)

    const footer: HTMLDivElement = (
        <div className="sidebar-footer">
            <div className="user-row">
                <div className="user-avatar">
                    <span className="material-symbols-rounded">person</span>
                </div>
                <div className="user-info">
                    <span className="user-name">Producer</span>
                    {creditsEl}
                </div>
            </div>
        </div>
    ) as unknown as HTMLDivElement

    // Back to MusicFlow Basic link
    const backLink: HTMLAnchorElement = (
        <a className="back-link" href="https://app.trymusicflow.com" target="_blank" title="Back to MusicFlow Basic">
            <span className="material-symbols-rounded back-icon">arrow_back</span>
            <span className="back-text">MusicFlow Basic</span>
        </a>
    ) as unknown as HTMLAnchorElement

    container.appendChild(logoRow)
    container.appendChild(collapseBtn)
    container.appendChild(nav)
    container.appendChild(backLink)
    container.appendChild(footer)

    RouteLocation.get().catchupAndSubscribe(() => updateActiveState())

    return container as unknown as HTMLElement
}
